import { useCallback, useState } from "react";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";

export interface UseDeleteDialogOptions {
    //url is the collection endpoint; the id is appended to it
    url: string
    //label names the thing in the confirmation and in both notifications, e.g.
    //"mask format"
    label: string
    //onDeleted usually reloads the list
    onDeleted: () => void
    //message overrides the default confirmation sentence
    message?: (name: string) => string
}

export interface DeleteDialog {
    //ask opens the confirmation for one row
    ask: (name: string, id: string) => void
    close: () => void
    deleting: boolean
    //dialogProps spreads onto ConfirmDialog; the caller supplies the title
    dialogProps: {
        open: boolean
        message: string
        loading: boolean
        onConfirm: () => void
        onCancel: () => void
    }
}

//useDeleteDialog owns the confirm-then-DELETE-then-reload flow that every list
//page repeats: which row is pending, the sentence shown, the request, and the
//success or failure notification.
export function useDeleteDialog(options: UseDeleteDialogOptions): DeleteDialog {
    const login = useLogin();
    const notification = useNotification();

    const [pending, setPending] = useState<{ name: string, id: string }>();
    const [deleting, setDeleting] = useState(false);

    const close = useCallback(() => setPending(undefined), []);

    const ask = useCallback((name: string, id: string) => setPending({ name, id }), []);

    const confirm = useCallback(async () => {
        const req = login.GetAxios();

        if (!req || !pending) {
            return; //not logged in, or nothing pending
        }

        setDeleting(true);

        try {
            await req.delete(`${options.url}/${pending.id}`);
            notification.Display({
                type: "success",
                title: "Success",
                message: `${options.label} deleted successfully`,
            });
            options.onDeleted();
        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: `Could not delete ${options.label}`,
                type: "error",
            });
        }

        setDeleting(false);
        setPending(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending, options.url, options.label, options.onDeleted]);

    const name = pending?.name || "";

    return {
        ask,
        close,
        deleting,
        dialogProps: {
            open: pending !== undefined,
            message: options.message
                ? options.message(name)
                : `Are you sure you want to delete the ${options.label} "${name}"?`,
            loading: deleting,
            onConfirm: confirm,
            onCancel: close,
        },
    };
}
